import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2, FiSearch } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { formatMoney, formatDate } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';

export default function Advances() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.advances.list(params);
      const paginated = data.advances || data.data || data;
      const list = paginated?.data || paginated || [];
      setRows(Array.isArray(list) ? list : []);
      setMeta(paginated?.current_page
        ? { current_page: paginated.current_page, last_page: paginated.last_page, total: paginated.total, per_page: paginated.per_page, per_page_options: paginated.per_page_options || data.per_page_options }
        : null);
    } catch (err) {
      showError('Failed to load advances', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete(`advance for "${row.employee?.name || 'employee'}"`);
    if (!res.isConfirmed) return;
    try {
      await api.advances.remove(row.id);
      toast('success', '', 'Advance cancelled');
      load();
    } catch (err) {
      showError('Failed to cancel advance', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Employee Advances"
        subtitle="Track advances given to employees before payroll."
        crumbs={[{ label: 'HR' }, { label: 'Advances' }]}
        actions={hasPermission('create-employees') && (
          <button className="btn btn-primary" onClick={() => navigate('/advances/new')}><FiPlus /> Give Advance</button>
        )}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="employees" label="advances" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No advances found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'employee', label: 'Employee', render: (r) => <b>{r.employee?.name || '-'}</b> },
              { key: 'date', label: 'Date', render: (r) => formatDate(r.date) },
              { key: 'amount', label: 'Amount', render: (r) => formatMoney(r.amount) },
              { key: 'deducted_amount', label: 'Deducted', render: (r) => formatMoney(r.deducted_amount) },
              { key: 'remaining', label: 'Remaining', render: (r) => formatMoney((r.amount || 0) - (r.deducted_amount || 0)) },
              { key: 'paid_via', label: 'Paid Via', render: (r) => <StatusBadge status={r.paid_via} /> },
              { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  {r.status === 'active' && r.deducted_amount === 0 && hasPermission('delete-employees') && (
                    <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                  )}
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}
